/* global globalThis:readonly */

export const idOf = value => String(value?._id ?? value?.id ?? value ?? '');
export const playerName = p => [p.lastname, p.firstname].filter(Boolean).join(' ');
export const sortPlayers = players => [...players].sort((a, b) =>
  (a.lastname || '').localeCompare(b.lastname || '', 'fr', { sensitivity: 'base' }) ||
  (a.firstname || '').localeCompare(b.firstname || '', 'fr', { sensitivity: 'base' }) || idOf(a).localeCompare(idOf(b)));
export function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
export function dateOnly(value) {
  if (!value) return '';
  if (value instanceof Date) return `${value.getFullYear()}-${String(value.getMonth()+1).padStart(2,'0')}-${String(value.getDate()).padStart(2,'0')}`;
  return String(value).slice(0,10);
}
export function previousDay(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) throw new Error('Date invalide.');
  const d = new Date(`${value}T12:00:00Z`);
  if (!Number.isFinite(d.getTime()) || d.toISOString().slice(0,10) !== value) throw new Error('Date invalide.');
  d.setUTCDate(d.getUTCDate()-1);
  return d.toISOString().slice(0,10);
}
export const activeMemberships = (p, date = today()) => (p.teamsSeason || []).filter(t =>
  (!t.startDate || dateOnly(t.startDate) <= date) && (!t.endDate || dateOnly(t.endDate) >= date));
export const operationId = () => globalThis.crypto.randomUUID();
export function validateTransfer(draft, context) {
  if (!context.provider || !context.discipline || !idOf(context.team)) throw new Error('Sélectionnez le contexte et une équipe.');
  const { player, sourceTeam, destinationTeam, startDate, kind } = draft;
  const destinationUnknown = kind === 'departure' && !!draft.destinationUnknown;
  const endDate = previousDay(startDate);
  if (!player?.lastname?.trim()) throw new Error('Le nom du joueur est obligatoire.');
  if (!draft.isNew && !idOf(player)) throw new Error('Identifiant du joueur manquant.');
  if (idOf(player.provider) !== idOf(context.provider) || idOf(player.discipline) !== idOf(context.discipline)) throw new Error('Le joueur ne correspond pas au groupe ou à la discipline sélectionnés.');
  if (kind === 'departure' && idOf(sourceTeam) !== idOf(context.team)) throw new Error('Équipe de départ incorrecte.');
  if (!destinationUnknown && !idOf(destinationTeam)) {
    if (kind === 'departure') throw new Error(`Pour valider le départ de ${playerName(player)}, sélectionnez une équipe d’arrivée ou cochez « Équipe d’arrivée non renseignée ».`);
    throw new Error('Choisissez une équipe d’arrivée.');
  }
  if (kind === 'arrival' && idOf(destinationTeam) !== idOf(context.team)) throw new Error('Équipe d’arrivée incorrecte.');
  if (!destinationUnknown && idOf(sourceTeam) && idOf(sourceTeam) === idOf(destinationTeam)) throw new Error('Les deux équipes doivent être différentes.');
  if (!destinationUnknown && activeMemberships(player, startDate).some(t => idOf(t.team) === idOf(destinationTeam))) throw new Error('Ce joueur appartient déjà à l’équipe d’arrivée à cette date.');
  if (idOf(sourceTeam)) {
    const matches = (player.teamsSeason || []).filter(t => idOf(t.team) === idOf(sourceTeam) && (!t.endDate || dateOnly(t.endDate) >= startDate));
    if (matches.length !== 1) throw new Error('Historique de départ absent ou ambigu : corrigez la fiche du joueur.');
    if (matches[0].startDate && dateOnly(matches[0].startDate) > endDate) throw new Error('La fin à J−1 précède le début dans l’équipe de départ.');
  }
  return true;
}
// Pure : ne modifie jamais le joueur chargé ni ses autres affiliations.
export function buildPlayerPayload(draft, context) {
  validateTransfer(draft, context);
  const { player, sourceTeam, destinationTeam, startDate } = draft;
  const destinationUnknown = draft.kind === 'departure' && !!draft.destinationUnknown;
  const teamsSeason = (player.teamsSeason || []).map(t => ({
    ...(t._id ? { _id: t._id } : {}), team: idOf(t.team),
    startDate: dateOnly(t.startDate) || null,
    endDate: idOf(t.team) === idOf(sourceTeam) && (!t.endDate || dateOnly(t.endDate) >= startDate)
      ? previousDay(startDate) : dateOnly(t.endDate) || null,
  }));
  if (!destinationUnknown) teamsSeason.push({ team: idOf(destinationTeam), startDate, endDate: null });
  // Dans FormPlayer, teams conserve toutes les équipes de l’historique.
  const teams = [...new Set([...(player.teams || []).map(idOf), ...teamsSeason.map(t => t.team)].filter(Boolean))];
  const payload = { ...player, provider: idOf(context.provider), discipline: idOf(context.discipline), teams, teamsSeason };
  if (draft.isNew) { delete payload._id; delete payload.id; }
  return payload;
}
